import { PolicyModeEnum, PolicyTypeEnum } from './constants';
import {
  CorrelatedExpression,
  CorrelatedRuleShallow,
} from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';
import { ExpressionCriteriaEnum } from '../DataLossPrevention/DataIdentifiers/type';

export interface PolicyListResDto {
  [key: string]: PolicyItem[];
}

export type EnableDisableStatus =
  | EnableDisableEnum.disabled
  | EnableDisableEnum.enabled;
export enum EnableDisableEnum {
  disabled = 0,
  enabled = 1,
}
export interface Target {
  title: string;
  key: string;
  folder?: boolean;
  lazy?: boolean;
}

interface BasicATPRuleFlag {
  isAsEnabled: boolean; //anti_spam_enabled
  isVsEnabled: boolean; //security_risk_scan_enable
  isFbEnabled: boolean; //file_block_enable
  isWtpEnabled: boolean; //web_reputation_enable
  isDdaEnabled: boolean; //virtual_analyzer_enable
  isRaiEnabled: boolean; //risk_ai_enable
}

interface BasicDLPRuleFlag {
  isDpEnabled: boolean; //dp_enable
  isKfEnabled: boolean; //keyword_extraction_enabled
  isSlfEnabled: boolean; //box sharelink_enabled
}

export enum ActionEnum {
  passWithoutLogging = 0,
  sanitizeFile = 2,
  tagSubject = 5,
  pass = 6,
  replaceWithTextFile = 10,
  delete = 103,
  quarantine = 109,
  passwordQuarantine = 119,
  removeLink = 203,
  moveToJunkEmailFolder = 207,
  addDisclaimer = 209,
  labelEmail = 220,
  moveToSpam = 221,
  block = 249,
  tagFileName = 251,
  applySensitivityLabel = 252,
  removeSensitivityLabel = 253,
  changeRecipient = 257,
}
export interface ActionData {
  threatType: string;
  threatDescription?: string;
  action: ActionEnum;
  // actionHandler: Function;
  actions: ActionEnum[];
  notifyType: EnableDisableStatus;
  // notifyTypeHandler: Function;
  actionKey: string;
  notifyTypeKey: string;
  handler: Function;
  suspiciousTagKey?: string;
  suspiciousTag?: string;
  addDisclaimerKey?: string;
  addDisclaimerValue?: string;
  otherSettings?: {
    value: number;
    title: string;
    otherSettingKey: string;
    disabled?: boolean;
  };
  info?: string;
  actionTypeDisabled?: boolean;
}
export interface RuleFlag extends BasicATPRuleFlag, BasicDLPRuleFlag {
  isWsEnabled: boolean;
  isMipEnabled: boolean;
  boxAdvancedFeatureEnabled: boolean;
  isSoEnabled: boolean;
  isEncryptionEnabled: boolean;
}

export interface PolicyItem extends RuleFlag {
  policyTargetType?: string;
  policyPriority: number;
  policyUuid: string;
  policyDescription: string;
  policyName: string;
  policyTarget: Targets;
  policyTargetSalesforce: SalesforceTargets | null;
  policyEncrypt: number;
  policyIsglobal: number;
  /**@description 0: disabled  1: enabled */
  policyRealtimeEnabled: number;
  policyType: PolicyTypeEnum;
  policyholderId: number;
  policyLang: string;
  updateTime: string;
  createdAt?: string;
  policyRmsEnabled: number;
  isSkipFolderEnabled: number;
  policyMode: PolicyModeEnum;
  policySubType: 0 | 1; //outbound or inbound
  holderType: number;
  scanHistory: ScanHistory;
  policyTypeString: string;
  policyHolderType: PolicyHolderTypeEnum;
  minStart?: string;
  taskState?: string;
  taskParam?: string;
}

interface ScanHistory {
  [key: string]: any;
}

export interface PolicyItemInTable extends Omit<PolicyItem, 'policyTarget'> {
  policyTarget: string;
  /**@description just record the policy target is none or not, use to disable the enable action of policy realtime scan*/
  policyTargetCapacity: 'none' | '';
  [key: string]: any;
}

export interface TargetGroup {
  title: string;
  key: string;
  children?: Targets;
}
export interface TargetUser {
  title: string;
  key: string;
  firstname: string;
  lastname: string;
  email: string;
}

export enum TargetCapacity {
  all = 'all',
  normal = 'normal',
  empty = 'empty',
}
export interface Targets {
  targetGroups: TargetGroup[];
  targets: TargetUser[];
  capacity?: TargetCapacity;
  targetType?: string;
  pagination?: {
    userOffset: number;
    userTotalCount: number;
    groupOffset: number;
    groupTotalCount: number;
    next: number;
  };
  // init is true means a empty policy, it only use in frontend
  empty?: true;
}

export interface SalesforceTargets {
  targetProfiles: Targets;
  targetCommunities: Targets;
  targetObjects: Targets;
}

export interface ExchangeOnlinePolicyItem extends PolicyItem {
  [PolicyFilters.tmAntispamfilters]: AntiSpamFilter;
  [PolicyFilters.tmFileblockings]: FileBlockingFilter;
  [PolicyFilters.tmVirusscans]: VirusScanFilter;
  [PolicyFilters.tmWebthreatprotections]: WebThreatProtectionFilter;
  [PolicyFilters.tmDdafilters]: DdaFilter;
  [PolicyFilters.tmRiskAiFilters]: RiskAiFilter;
}

export interface ExchangeOnlinePolicyState extends ExchangeOnlinePolicyItem {
  availableTargets: Targets;
}
export interface RecipientGroup {
  id: number;
  recipientGroupName: string;
  recipientNumber: number;
  isDefault: boolean;
}
export interface Notification {
  ntfAdminIntervalNum: number;
  ntfAdminIntervalType: number;
  ntfAdminMessage: string;
  ntfAdminNotifySettingType: number;
  ntfAdminOccurenceNum: number;
  ntfAdminSubject: string;
  ntfEnableEndUserRestore: number;
  ntfDoNotNotifyExternalUser: number;
  ntfEnableNotifyAdmin: EnableDisableStatus;
  ntfEnableNotifyRecipient: EnableDisableStatus;
  ntfInternalRecipientMessage: string;
  ntfInternalRecipientSubject: string;
  ntfToAccount: string;
  ntfRecipientGroupId: number;
  ntfRecipientGroupEnabled: EnableDisableStatus;
}
export enum AsActionKeys {
  asActionBec = 'asActionBec',
  asActionOthers = 'asActionOthers',
  asActionPhish = 'asActionPhish',
  asActionRansome = 'asActionRansome',
  asActionGrayEmail = 'asActionGrayEmail',
  asActionMalicious = 'asActionMalicious',
  asActionBlockedSender = 'asActionBlockedSender',
  asActionScam = 'asActionScam',
  asActionSuspiciousSender = 'asActionSuspiciousSender',
}
export enum AsNotifyTypeKeys {
  asBecNotifyType = 'asBecNotifyType',
  asOthersNotifyType = 'asOthersNotifyType',
  asPhishNotifyType = 'asPhishNotifyType',
  asRansomeNotifyType = 'asRansomeNotifyType',
  asMaliciousNotifyType = 'asMaliciousNotifyType',
  asBlockedSenderNotifyType = 'asBlockedSenderNotifyType',
  asGrayEmailNotifyType = 'asGrayEmailNotifyType',
  asScamNotifyType = 'asScamNotifyType',
  asSuspiciousSenderNotifyType = 'asSuspiciousSenderNotifyType',
}
export enum AsSuspiciousTagKeys {
  asSuspiciousTagBec = 'asSuspiciousTagBec',
  asSuspiciousTagOthers = 'asSuspiciousTagOthers',
  asSuspiciousTagPhish = 'asSuspiciousTagPhish',
  asSuspiciousTagRansome = 'asSuspiciousTagRansome',
  asSuspiciousTagGrayEmail = 'asSuspiciousTagGrayEmail',
  asSuspiciousTagMalicious = 'asSuspiciousTagMalicious',
  asSuspiciousTagBlockedSender = 'asSuspiciousTagBlockedSender',
  asSuspiciousTagScam = 'asSuspiciousTagScam',
  asSuspiciousTagSuspiciousSender = 'asSuspiciousTagSuspiciousSender',
}
export enum AsAddDisclaimerKeys {
  asDisclaimerGrayEmail = 'asDisclaimerGrayEmail',
  asDisclaimerScam = 'asDisclaimerScam',
  asDisclaimerBec = 'asDisclaimerBec',
  asDisclaimerPhish = 'asDisclaimerPhish',
  asDisclaimerRansome = 'asDisclaimerRansome',
  asDisclaimerMalicious = 'asDisclaimerMalicious',
  asDisclaimerOtherSpam = 'asDisclaimerOtherSpam',
  asDisclaimerSuspiciousSender = 'asDisclaimerSuspiciousSender',
}

export enum AsActionToDisclaimerMap {
  asActionGrayEmail = 'asDisclaimerGrayEmail',
  asActionScam = 'asDisclaimerScam',
  asActionBec = 'asDisclaimerBec',
  asActionPhish = 'asDisclaimerPhish',
  asActionRansome = 'asDisclaimerRansome',
  asActionMalicious = 'asDisclaimerMalicious',
  asActionOthers = 'asDisclaimerOtherSpam',
  asActionSuspiciousSender = 'asDisclaimerSuspiciousSender',
}

export enum ApplyBoundEnum {
  incomming = 1,
  sentMessageOnly = 2,
  all = 7,
}
export enum SpamLevelEnum {
  low = 2,
  medium = 4,
  high = 6,
}
export type WsNotifyRecipientActionTypeEnum = Exclude<
  ActionEnum,
  | ActionEnum.passWithoutLogging
  | ActionEnum.sanitizeFile
  | ActionEnum.replaceWithTextFile
>;
export interface AntiSpamFilter {
  asActionBec: ActionEnum;
  asActionOthers: ActionEnum;
  asActionPhish: ActionEnum;
  asActionRansome: ActionEnum;
  asGreyMailEnabled: EnableDisableStatus;
  asActionMalicious: ActionEnum;
  asActionBlockedSender: ActionEnum;
  asActionGrayEmail: ActionEnum;
  asActionScam: ActionEnum;
  wsNotifyRecipientActionType: WsNotifyRecipientActionTypeEnum;
  asApprovedSenderEmailEnabled: number;
  asApprovedSenderEmailEntries: string;
  asBlockedSenderEmailEnabled: number;
  asBlockedSenderEmailEntries: string;
  asBecNotifyType: EnableDisableStatus;
  asApplyBound: ApplyBoundEnum;
  asSkipInternalSpamDetection: EnableDisableStatus;
  asEnabled: EnableDisableStatus;
  wsEnabled: number;
  asOthersNotifyType: EnableDisableStatus;
  asPhishNotifyType: EnableDisableStatus;
  asRansomeNotifyType: EnableDisableStatus;
  asMaliciousNotifyType: EnableDisableStatus;
  asBlockedSenderNotifyType: EnableDisableStatus;
  asGrayEmailNotifyType: EnableDisableStatus;
  asScamNotifyType: EnableDisableStatus;
  asGrayEmailMmlEnabled: number;
  asGrayEmailSocialNetworkEnabled: number;
  asGrayEmailForumEnabled: number;
  asGrayEmailBulkEnabled: number;
  asNewbornDetectionEnabled: EnableDisableEnum;
  wsNotifyExpectedSenderEnabled: number;
  wsNotifyExpectedSenderAttachOriginMailEnabled: number;
  wsNotifyExpectedSenderWithFeedbackEnabled: number;
  ntfWritingStyleToExpectedSenderFeedbackMessage: string;
  wsNotifyAdministratorEnabled: number;
  wsNotifyAdministratorAttachOriginMailEnabled: number;
  ntfWritingStyleExpectedSenderSubject: string;
  ntfWritingStyleExpectedSenderMessage: string;
  ntfWritingStyleToAdminAccount: string;
  ntfWritingStyleAdministratorSubject: string;
  ntfWritingStyleAdministratorMessage: string;
  asSpamLevel: SpamLevelEnum;
  asSnapLevel: number;
  asSuspiciousTagBec: string;
  asSuspiciousTagOthers: string;
  asSuspiciousTagPhish: string;
  asSuspiciousTagRansome: string;
  asSuspiciousTagMalicious: string;
  asSuspiciousTagBlockedSender: string;
  wsSuspiciousTag: string;
  asSuspiciousTagGrayEmail: string;
  asSuspiciousTagScam: string;
  wsDisclaimer: string;
  asEnabledFeedback: number;
  asApprovedHeaderEnabled: number;
  asApprovedHeaderList: string;
  asBlockedHeaderEnabled: number;
  asBlockedHeaderRuleList: string;
  asSpoofDetectEnabled: number;
  asRetroScanEnabled: number;
  asAggressiveHpuEnabled: number;
  asUnusualSignalEnabled: EnableDisableEnum;
  asSuspiciousTagSuspiciousSender: string;
  asActionSuspiciousSender: number;
  asSuspiciousSenderNotifyType: EnableDisableStatus;
  asAggressiveNameSpoofingEnabled: number;
  asDisclaimerGrayEmail: string;
  asDisclaimerScam: string;
  asDisclaimerBec: string;
  asDisclaimerPhish: string;
  asDisclaimerRansome: string;
  asDisclaimerMalicious: string;
  asDisclaimerOtherSpam: string;
  asDisclaimerSuspiciousSender: string;
  notification: Notification;
}

export interface FileBlockingFilter {
  fbActionType: number;
  fbCompressedFileScanningType: number;
  fbDecompressLayerLimit: number;
  fbEnabled: EnableDisableStatus;
  fbExceptionFileExtensionEnabled: number;
  fbExceptionFileExtensionBlocked: number;
  fbExceptionFileNameBlocked: number;
  fbExceptionFileNameEnabled: number;
  fbExceptionFileTypeEnabled: number;
  fbBlockFileExtensionBlocked: number;
  fbBlockFileExtensionEnabled: number;
  fbBlockFileNameBlocked: number;
  fbBlockFileNameEnabled: number;
  fbBlockFileTypeEnabled: number;
  fbExtractFileCountLimit: number;
  fbExtractFileRatioLimit: number;
  fbExtractFileSizeLimit: number;
  fbFileExtensionBlocked: number;
  fbFileNameBlocked: number;
  fbFileNameEnabled: number;
  fbFileTypeEnabled: number;
  fbNotifyType: EnableDisableStatus;
  fbReplacementFileName: string;
  fbReplacementText: string;
  fbScanningType: number;
  fbTransportEnabled: number;
  fbApprovedSenderEmailEnabled: number;
  fbApprovedSenderEmailEntries: string;
  fbApplyBound: ApplyBoundEnum;
  fbSecondActionEnabled: number;
  fbSecondActionType: number;
  fbExceptionFileTypeList: string;
  fbExceptionFileExtensionList: string;
  fbExceptionFileNameList: string;
  fbBlockFileNameList: string;
  fbBlockFileExtensionList: string;
  fbBlockFileTypeList: string;
  fbAdvancedIncomingRuleEnabled: EnableDisableStatus;
  fbIncomingRuleBlockFileTypeEnabled: EnableDisableStatus;
  fbIncomingRuleBlockFileTypeList: string;
  fbIncomingRuleBlockFileExtensionEnabled: EnableDisableStatus;
  fbIncomingRuleBlockFileExtensionList: string;
  fbIncomingRuleBlockFileNameEnabled: EnableDisableStatus;
  fbIncomingRuleBlockFileNameList: string;
  notification: Notification;
  fbFileTagContent: string;
}
export enum VsActionKeys {
  vsDisarmActionType = 'vsDisarmActionType',
  vsVirusesActionType = 'vsVirusesActionType',
  vsWormsTrojansActionType = 'vsWormsTrojansActionType',
  vsPackersActionType = 'vsPackersActionType',
  vsMaliciousActionType = 'vsMaliciousActionType',
  vsAdditionalThreatsActionType = 'vsAdditionalThreatsActionType',
  vsRansomwareActionType = 'vsRansomwareActionType',
  vsOverRestrictionActionType = 'vsOverRestrictionActionType',
  vsOverFilesizeRestrictionActionType = 'vsOverFilesizeRestrictionActionType',
  vsEncryptedCompressedFileActionType = 'vsEncryptedCompressedFileActionType',
  vsOtherEncryptedFileActionType = 'vsOtherEncryptedFileActionType',
}

export enum VsNotifyTypeKeys {
  vsDisarmNotifyType = 'vsDisarmNotifyType',
  vsVirusesNotifyType = 'vsVirusesNotifyType',
  vsWormsTrojansNotifyType = 'vsWormsTrojansNotifyType',
  vsPackersNotifyType = 'vsPackersNotifyType',
  vsMaliciousNotifyType = 'vsMaliciousNotifyType',
  vsAdditionalThreatsNotifyType = 'vsAdditionalThreatsNotifyType',
  vsRansomwareNotifyType = 'vsRansomwareNotifyType',
  vsOverRestrictionNotifyType = 'vsOverRestrictionNotifyType',
  vsOverFilesizeRestrictionNotifyType = 'vsOverFilesizeRestrictionNotifyType',
  vsEncryptedCompressedFileNotifyType = 'vsEncryptedCompressedFileNotifyType',
  vsOtherEncryptedFileNotifyType = 'vsOtherEncryptedFileNotifyType',
}

export enum VsSuspiciousTagKeys {
  vsTextTagSubjectContent = 'vsTextTagSubjectContent',
}
export interface VirusScanFilter {
  vsActionType: number;
  vsActiveActionNotifyType: EnableDisableStatus;
  vsAdditionalThreats: number;
  vsAdditionalThreatsActionType: number;
  vsAdditionalThreatsNotifyType: EnableDisableStatus;
  vsAllThreatsActionType: number;
  vsAllThreatsNotifyType: EnableDisableStatus;
  vsBodysizeMaximum: number;
  vsCompressedFilecountMaximum: number;
  vsCompressedFilelayerMaximum: number;
  vsCompressedFilesizeMaximum: number;
  vsCompressedRatioMaximum: number;
  vsDefaultScanningType: number;
  vsDetectedThreatType: number;
  vsEnableDonotCleanCompressedFile: number;
  vsEnableHeuristicScanOnMacro: number;
  vsEnableIntellitrap: number;
  vsEnableMacroStripping: number;
  vsEnableMassingMailing: number;
  vsEnableScanFileExt: number;
  vsEnablePredictiveMachineLearningFeedback: number;
  vsEnablePredictiveMachineLearning: number;
  vsEnableScanMessagebody: number;
  vsEnableVirusscan: EnableDisableStatus;
  vsEncryptedFileActionType: number;
  vsEncryptedCompressedFileActionType: number;
  vsOtherEncryptedFileActionType: number;
  vsEncryptedFileNotifyType: EnableDisableStatus;
  vsEncryptedCompressedFileNotifyType: EnableDisableStatus;
  vsOtherEncryptedFileNotifyType: EnableDisableStatus;
  vsReplacementFileNameUnscannable: string;
  vsReplacementTextUnscannable: string;
  vsFilesizeMaximum: number;
  vsGenericsActionType: number;
  vsGenericsNotifyType: EnableDisableStatus;
  vsHeuristicScanActionLevel: number;
  vsHeuristicScanActionType: number;
  vsRansomwareActionType: number;
  vsRansomwareNotifyType: EnableDisableStatus;
  vsMaliciousActionType: number;
  vsMaliciousNotifyType: EnableDisableStatus;
  vsMassingMailingActionType: number;
  vsMassingMailingNotifyType: EnableDisableStatus;
  vsNeedToCheckAttachmentSize: number;
  vsNeedToCheckBodySize: number;
  vsNeedToCheckComprFileLayer: number;
  vsNeedToCheckComprFileRatio: number;
  vsNeedToCheckDecomFileCount: number;
  vsNeedToCheckDecomFileSize: number;
  vsNeedToCheckEntityCount: number;
  vsNeedToCheckOleLayer: number;
  vsOleLayerMaximum: number;
  vsOverRestrictionActionType: number;
  vsOverRestrictionNotifyType: EnableDisableStatus;
  vsPackersActionType: number;
  vsPackersNotifyType: EnableDisableStatus;
  notification: Notification;
  vsApplyBound: ApplyBoundEnum;
  vsDisarmEnabled: number;
  vsDisarmExceptEnabled: number;
  vsDisarmActionType: number;
  vsDisarmNotifyType: EnableDisableStatus;
  vsNonOfficeDisarmEnabled: EnableDisableStatus;
  vsVirusesActionType: number;
  vsVirusesNotifyType: EnableDisableStatus;
  vsWormsTrojansActionType: number;
  vsWormsTrojansNotifyType: EnableDisableStatus;
  vsOverFilesizeRestrictionActionType: number;
  vsOverFilesizeRestrictionNotifyType: EnableDisableStatus;
  vsReplacementFileName: string;
  vsReplacementText: string;
  vsTextTagSubjectContent: string;
  vsSpecifiedFileTypeObjects: string;
  vsSpecifiedFileExtObjects: string;
  vsDisarmExceptSenderString: string;
  vsDisarmExceptReceiverString: string;
  vsSecondActionEnabled: number;
  vsSecondActionType: number;
  vsFileTagContent: string;
}

export enum HeaderOperatorEnum {
  equals = 'equals',
  contains = 'contains',
}
export interface ApprovedHeader {
  key: string;
  value: string;
  operator: HeaderOperatorEnum;
}
export interface WebThreatProtectionFilter {
  wtpActionType: number;
  wtpActionBlockedUrl: number;
  wtpApprovedSenderEmailEnabled: number;
  wtpApprovedSenderEmailEntries: string;
  wtpApprovedUrlListEnabled: number;
  wtpApprovedUrlListEntries: string;
  wtpBlockedUrlListEnabled: number;
  wtpBlockedUrlListEntries: string;
  wtpEnabled: EnableDisableStatus;
  wtpSecuritylevelEnabled: number;
  wtpUrlcategoryEnabled: number;
  wtpUrlcategoryEntries: string;
  wtpExtractAttachmentUrl: number;
  wtpNotifyType: EnableDisableStatus;
  wtpBlockedUrlNotifyType: EnableDisableStatus;
  wtpSecurityLevel: number;
  wtpSuspiciousTag: string;
  wtpSuspiciousTagBlockedUrl: string;
  wtpUnratedAction: number;
  wtpReplacementText: string;
  wtpApplyBound: ApplyBoundEnum;
  wtpInternalDomainToApproveUrl: number;
  wtpDynamicScanEnabled: number;
  wtpUrlcvEnabled: number;
  wtpApprovedHeaderEnabled: number;
  wtpApprovedHeaderList: string;
  wtpRetroScanEnabled: number;
  wtpTimeOfClickEnabled: number;
  wtpTimeOfClickApplyType: number;
  wtpFileTagEnabled: number;
  wtpFileAction: number;
  wtpFileActionNotifyType: EnableDisableStatus;
  wtpSecondActionEnabled: number;
  wtpSecondActionType: number;
  notification: Notification;
  wtpTocApprovedUrlListEnabled: number;
  wtpTocApprovedUrlListEntries: string;
  wtpFileTagContent: string;
}
export enum DdaActionKeys {
  ddaActionHighRisk = 'ddaActionHighRisk',
  ddaActionMediumRisk = 'ddaActionMediumRisk',
  ddaActionLowRisk = 'ddaActionLowRisk',
  ddaActionUnknowRisk = 'ddaActionUnknowRisk',
}
export enum DdaNotifyTypeKeys {
  ddaHighRiskNotifyType = 'ddaHighRiskNotifyType',
  ddaMediumRiskNotifyType = 'ddaMediumRiskNotifyType',
  ddaLowRiskNotifyType = 'ddaLowRiskNotifyType',
  ddaUnknowRiskNotifyType = 'ddaUnknowRiskNotifyType',
}
export enum DdaSuspiciousTagKeys {
  ddaSuspiciousTagHighRisk = 'ddaSuspiciousTagHighRisk',
  ddaSuspiciousTagMediumRisk = 'ddaSuspiciousTagMediumRisk',
  ddaSuspiciousTagLowRisk = 'ddaSuspiciousTagLowRisk',
  ddaSuspiciousTagUnknowRisk = 'ddaSuspiciousTagUnknowRisk',
}
export interface DdaFilter {
  ddaActionHighRisk: number;
  ddaActionLowRisk: number;
  ddaActionMediumRisk: number;
  ddaApplyActionUnscannable: number;
  ddaApplyActionUnscannableEnabled: number;
  ddaApplyDiffActionKnownThread: number;
  ddaApplyDiffActionKnownThreadEnabled: number;
  ddaEnabled: EnableDisableStatus;
  ddaFileEnabled: number;
  ddaURLEnabled: number;
  ddaQuarantine: number;
  ddaSuspiciousTagHighRisk: string;
  ddaSuspiciousTagMediumRisk: string;
  ddaSuspiciousTagLowRisk: string;
  ddaActionUnknowRisk: number;
  ddaSuspiciousTagUnknowRisk: string;
  ddaActionUnknowRiskEnabled: number;
  ddaHighRiskNotifyType: EnableDisableStatus;
  ddaMediumRiskNotifyType: EnableDisableStatus;
  ddaLowRiskNotifyType: EnableDisableStatus;
  ddaUnknowRiskNotifyType: EnableDisableStatus;
  ddaReplacementText: string;
  ddaApplyBound: ApplyBoundEnum;
  ddaApprovedSenderEmailEnabled: number;
  ddaMonitorModeEnabled: number;
  ddaFileTempQuarantineEnabled: EnableDisableStatus;
  ddaURLTempQuarantineEnabled: EnableDisableStatus;
  ddaApprovedSenderEmailEntries: string;
  ddaApprovedFileNameEnabled: number;
  ddaApprovedFileNameEntries: string;
  notification: Notification;
}

export interface RiskAiFilterRule {
  cfgIndex: string;
  raiRuleId: string;
  raiRuleAction: ActionEnum;
  raiRuleSubjectTag: string;
  raiRuleDisclaimer: string;
}

export type MergedRiskAiFilterRule = RiskAiFilterRule & {
  tmRaiRule: CorrelatedRuleShallow;
};

export enum RiskAiFilterRuleScope {
  SpecificRules = 0,
  AllRules = 1,
}

export interface RiskAiFilter {
  raiEnabled: number;
  raiAgenticAiEnabled: EnableDisableStatus;
  raiActionConfirmed: ActionEnum;
  raiSuspiciousTagConfirmed: string;
  raiConfirmedNotifyType: EnableDisableStatus;
  raiActionSpam: ActionEnum;
  raiSuspiciousTagSpam: string;
  raiSpamNotifyType: EnableDisableStatus;
  raiSecurityRisksAddBannerEnabled: EnableDisableStatus;
  raiAnomalyNotifyType: EnableDisableStatus;
  raiAnomalyAddBannerEnable: EnableDisableStatus;
  raiCustomNotifyType: EnableDisableStatus;
  raiNotificationInstanceCfgsetting: string;
  tmRaiFilterRules: MergedRiskAiFilterRule[];
  userDefinedExpressions: Pick<
    CorrelatedExpression,
    'raiExpId' | 'raiExpName' | 'raiExpContent'
  >[];
  raiApprovedSenderEmailEnabled: EnableDisableStatus;
  raiApprovedSenderEmailEntries: string;
  notification: Notification;
  raiAnomalyRuleScope: RiskAiFilterRuleScope;
  // Excluded rules
  raiAnomalyExpRules: string; // rule ids separated by ',' e.g. 'APN-01,APN-02'
  // Super Aggressive
  raiSuperAggActionSuspicious: ActionEnum;
  raiSuperAggActionUnwanted: ActionEnum;
  raiSuperAggSubjectTagSuspicious: string;
  raiSuperAggSubjectTagUnwanted: string;
  raiSuperAggDisclaimerSuspicious: string;
  raiSuperAggDisclaimerUnwanted: string;
  // Aggressive
  raiAggActionSuspicious: ActionEnum;
  raiAggActionUnwanted: ActionEnum;
  raiAggSubjectTagSuspicious: string;
  raiAggSubjectTagUnwanted: string;
  raiAggDisclaimerSuspicious: string;
  raiAggDisclaimerUnwanted: string;
  // Conservative
  raiConsActionSuspicious: ActionEnum;
  raiConsActionUnwanted: ActionEnum;
  raiConsSubjectTagSuspicious: string;
  raiConsSubjectTagUnwanted: string;
  raiConsDisclaimerSuspicious: string;
  raiConsDisclaimerUnwanted: string;
}

export enum ServiceCollection {
  Mail = 'mail',
  Collaboration = 'collaboration',
}

export enum OverAllPolicyType {
  ATP = '1',
  DLP = '2',
}

export enum PolicyListModalName {
  None = 'none',
  Scan = 'scan',
  Delete = 'delete',
  Scanning = 'scanning',
  CheckSkipped = 'checkSkipped',
  GmailNote = 'gmailNote',
}

export type PolicyListModalState =
  | {
      name: Exclude<PolicyListModalName, PolicyListModalName.None>;
      //policy: Partial<PolicyItem>;
      policy: PolicyItemInTable;
      policyManualScanRunningQueue?: any;
      scanHistory?: any;
    }
  | {
      name: PolicyListModalName.None;
      policy: null;
      policyManualScanRunningQueue?: any;
      scanHistory?: any;
    };

export enum ScanType {
  ScanAndProtect = '0',
  ScanOnly = '1',
}

export enum ScanScopeType {
  FixedDay = '0',
  Custom = '1',
}

export enum MailPolicyNameEnum {
  Exchange = 'exchange',
  Gmail = 'gmail',
  GmailInlineOutbound = 'gmailInlineOutbound',
  GmailInlineInbound = 'gmailInlineInbound',
  ExchangeInlineOutbound = 'exchangeInlineOutbound',
  ExchangeInlineInbound = 'exchangeInlineInbound',
}

export enum CollaborationPolicyNameEnum {
  SharePoint = 'sharePoint',
  OneDrive = 'oneDrive',
  Box = 'box',
  Dropbox = 'dropbox',
  GoogleDrive = 'googleDrive',
  SalesforceSandbox = 'salesforceSandbox',
  SalesforceProduction = 'salesforceProduction',
  Teams = 'teams',
  TeamsChat = 'teamsChat',
}

export enum TabsEnum {
  general = 'general',
  advancedSpamProtection = 'advancedSpamProtection',
  malwareScanning = 'malwareScanning',
  fileBlocking = 'fileBlocking',
  webReputation = 'webReputation',
  virtualAnalyzer = 'virtualAnalyzer',
  dlp = 'dlp',
  shareLink = 'shareLink',
  keywordExtraction = 'keywordExtraction',
  riskAi = 'riskAi',
}

// export enum FilterNotification {
//   AsNotification = 'asNotification',
//   VsNotification = 'vsNotification',
//   FbNotification = 'fbNotification',
//   WtpNotification = 'wtpNotification',
//   DdaNotification = 'ddaNotification',
// }

export enum PolicyFilters {
  tmAntispamfilters = 'tmAntispamfilters',
  tmVirusscans = 'tmVirusscans',
  tmFileblockings = 'tmFileblockings',
  tmWebthreatprotections = 'tmWebthreatprotections',
  tmDdafilters = 'tmDdafilters',
  tmRiskAiFilters = 'tmRiskAiFilters',
}
export enum DlpPolicyFilters {
  tmDpfilters = 'tmDpfilters',
  tmKeywordfilters = 'tmKeywordfilters',
  tmSlfFilters = 'tmSlfFilters',
}

export interface ErrorObj {
  title: string;
  content: string;
}

export enum PolicyHolderTypeEnum {
  ATP = 1,
  DLP = 2,
}

// exchange inline
export type ExchangeInlineInboundPolicyItem = ExchangeOnlinePolicyItem;
export type ExchangeInlineOutboundPolicyItem = ExchangeOnlinePolicyItem;

// DLP
export interface DlpPolicyGeneral extends RuleFlag {
  policyDescription: string;
  policyName: string;
  policyTarget: Targets;
  policyType: PolicyTypeEnum;
  policySubType: number;
  isSkipFolderEnabled: EnableDisableStatus;
  currentPriority: number;
  policyUuid: string;
  boxAdvancedFeatureEnabled: boolean;
  createdAt?: string;
  updateTime: string;
  groupCompanyGuid?: string;
  holderType: number;
  policyHolderType: PolicyHolderTypeEnum;
  policyEncrypt: number;
  policyIsglobal: EnableDisableStatus;
  policyLang: string;
  policyMode: PolicyModeEnum;
  policyPriority: number;
  policyRealtimeEnabled: EnableDisableStatus;
  policyRmsEnabled: EnableDisableStatus;
  policyholderId: number;
}
export interface DpFilter {
  dpEnabled: EnableDisableStatus;
  dpReplacementText: string;
  dpApplyBound: ApplyBoundEnum;
  dpSentItemEnabled: EnableDisableStatus;
  dpSentItemAction: number;
  dpViolationContentEnabled: EnableDisableStatus;
  dpViolationContentMode: number;
  dpFileAction: number;
  dpMipOverrideEnabled: EnableDisableStatus;
  dpMipBackupEnabled: EnableDisableStatus;
  dpMipBackupAction: number;
  dpSubjectScanEnabled: EnableDisableStatus;
  dpBodyScanEnabled: EnableDisableStatus;
  dpAttachmentScanEnabled: EnableDisableStatus;
  dpMailHeadersScanEnabled: EnableDisableStatus;
  dpMailHeaders: string;
  dpSecondActionEnabled: EnableDisableStatus;
  dpSecondActionType: number;
  notification: Notification;
  dpFileTagContent: string;
  changeRecipientDisclaimer: string;
  dpMipLabelId: string;
  dpMipLabelName: string;
  tmDpfilterrules: TmDpfilterRule[];
  changeRecipientEmailAddressList: string;
  tmMipLabelRules: TmMipLabelRule[];
  dpFileTagEnabled: number;
  dpApprovedSenderEmailEnabled: EnableDisableStatus;
  dpApprovedSenderEmailEntries: string;
}

export interface TmDpfilterRule {
  dpAction: ActionEnum;
  tmDpfiltertemplate: TmDpfilterTemplate;
}

export interface TmDpfilterTemplate {
  cfgIndex: string;
}

export interface TmMipLabelRule {
  dpAction: ActionEnum;
  tmMipLabel: TmMipLabel;
}

export interface TmMipLabel {
  labelId: string;
}

export enum DpBuiltinEnum {
  UserDefined = 0,
  Predefined = 1,
}

export interface DlpTemplate {
  cfgIndex: string;
  companyId: string;
  dpTemplateBuiltin: DpBuiltinEnum;
  dpTemplateDesc: string;
  dpTemplateName: string;
  dpTemplateIndustry?: string;
  dpTemplateRegion?: string;
  dpTemplateElement?: string;
}

export enum ComplianceRuleType {
  dlpTemplate = 'dlpTemplate',
  mipLabel = 'mipLabel',
}
export interface DlpTemplateItem extends DlpTemplate {
  key: string;
  title: string;
  type: ComplianceRuleType;
}
export enum BuildingBlockTypeEnum {
  entity = 'ENTITY',
  keyword = 'KEYWORD',
}
export enum DlpOperatorEnum {
  none = '',
  and = 'AND',
  or = 'OR',
  except = 'EXCEPT',
}
export interface BuildingBlock {
  sourceName: string;
  sourceType: BuildingBlockTypeEnum;
  displayName: string;
  entityHits?: number;
  operation?: DlpOperatorEnum;
}
export interface TemplateRule {
  buildingBlock: BuildingBlock[];
  operation: DlpOperatorEnum;
}

export interface DlpTemplateContent {
  matchRule: TemplateRule[];
  name?: string;
  displayName?: string;
  description?: string;
  preDefined?: boolean;
}
export interface DlpTemplateDetail {
  dpTemplateDesc: string;
  dpTemplateName: string;
  template: DlpTemplateContent;
  dpTemplateSeverity?: string;
  cfgIndex?: string;
  companyId?: string;
  dpTemplateBuiltin?: DpBuiltinEnum;
  dpTemplateIndustry?: string;
  dpTemplateRegion?: string;
  dpTemplateElement?: string;
}
/**
 * @description interface for dlp expression list
 */
export interface DlpExpression {
  cfgIndex: string;
  companyId: string;
  dpExpressionAlphabetcharset: string;
  dpExpressionBuiltin: DpBuiltinEnum;
  dpExpressionCasesensitive: number;
  dpExpressionDesc: string;
  dpExpressionDisplayformat: string;
  dpExpressionExample: string;
  dpExpressionInternalname: string;
  dpExpressionMaxleftradius: number;
  dpExpressionMaxlength: number;
  dpExpressionMaxrightradius: number;
  dpExpressionMinleftradius: number;
  dpExpressionMinlength: number;
  dpExpressionModified: number;
  dpExpressionName: string;
  dpExpressionPattern: string;
  dpExpressionSuffixlength: number;
  dpExpressionType: ExpressionCriteriaEnum;
  dpExpressionValidator: string;
}

export interface TmDlpExpressionValidator {
  dictClassify: string;
  dictDisplayvalue: string;
  dictId: number;
  dictKey: string;
  dictNo: number;
  dictValue: string;
}

/**
 * @description interface dlp expression edit or add
 */
export interface DlpExpressionDetail {
  cfgIndex?: string; //update时才有
  companyId?: string; //update时才有
  dpExpressionName: string;
  dpExpressionDesc: string;
  dpExpressionPattern: string;
  dpExpressionValidator: string;
  dpExpressionValidator_val: TmDlpExpressionValidator | string;
  dpExpressionType: ExpressionCriteriaEnum;
  dpExpressionAlphabetcharset: string;
  dpExpressionBuiltin?: number; //update时才有
  dpExpressionCasesensitive: number;
  dpExpressionDisplayformat: string;
  dpExpressionExample: string;
  dpExpressionInternalname?: string; //update时才有
  dpExpressionMaxleftradius: number;
  dpExpressionMaxlength: number;
  dpExpressionMaxrightradius: number;
  dpExpressionMinleftradius: number;
  dpExpressionMinlength: number;
  dpExpressionModified?: number; //update时才有
  dpExpressionSuffixlength: number;
}

export interface DlpExpressionAnalysis {
  dpExpressionBuiltin: number;
  dpExpressionCasesensitive: number;
  dpExpressionDesc: string;
  dpExpressionMaxleftradius: number;
  dpExpressionMaxlength: number;
  dpExpressionMaxrightradius: number;
  dpExpressionMinleftradius: number;
  dpExpressionMinlength: number;
  dpExpressionName: string;
  dpExpressionPattern: string;
  dpExpressionSuffixlength: number;
  dpExpressionType: ExpressionCriteriaEnum;
  dpExpressionValidator: string;
}

export interface DlpPolicyItem extends PolicyItem {
  [DlpPolicyFilters.tmDpfilters]: DpFilter;
  [DlpPolicyFilters.tmKeywordfilters]?: KeywordExtractionFilter;
  [DlpPolicyFilters.tmSlfFilters]?: ShareLinkFilter;
}

export type DlpExchangePolicyItem = DlpPolicyItem;

export interface TableColumn {
  id: string;
  header: string;
  accessorKey?: string;
  size?: number;
  cell?: Function;
  style?: Object;
}

export interface MipLabel {
  labelId: string;
  labelName: string;
  sensitivity: number;
}
export interface MipLabelItem extends MipLabel {
  key: string;
  title: string;
  type: ComplianceRuleType;
}

export interface FilterParams {
  keyword: string;
  offset: number;
  size: number;
}

/***********************************************************************************************************************
 * Dlp Keywords
 ***********************************************************************************************************************
 */
export interface DlpKeyword {
  cfgIndex: string;
  companyId: string;
  dpKeywordBuiltin: number;
  dpKeywordCondition: string;
  dpKeywordDesc: string;
  dpKeywordInternalname: string;
  dpKeywordModified: number;
  dpKeywordName: string;
  dpKeywordNumber: number;
  dpKeywordThreshold: number;
}

export interface DlpKeywordDictionaryKeyword {
  value: string;
  caseSensitive: boolean;
  score: number;
  description: string;
}
export interface DlpKeywordDetail {
  //edit的时候才有这些数据
  cfgIndex?: string;
  companyId?: string;
  dpKeywordInternalname?: string;
  dpKeywordModified?: number;
  dpKeywordName: string;
  dpKeywordDesc: string;
  dpKeywordBuiltin: number;
  dpKeywordCondition: string;
  dpKeywordThreshold: number;
  dpKeywordNumber: number;
  dictionary: {
    keyword?: DlpKeywordDictionaryKeyword[];
    [key: string]: any;
  };
}
/***********************************************************************************************************************
 * Dlp Keywords Extraction
 ***********************************************************************************************************************
 */
export enum KfFileTypeEnum {
  Text = 1,
  AdobePdf = 2,
  MicrosoftDoc = 3,
}
export enum KfContainTypeEnum {
  Contain = 0,
  NotContain = 1,
}
export enum KfDefaultLogicalOperatorEnum {
  And = 'AND',
  Or = 'OR',
}
export enum KfCollaborationActionTypeEnum {
  delete = ActionEnum.delete,
  quarantine = ActionEnum.quarantine,
  pass = ActionEnum.pass,
}
export interface KfContainRule {
  kfFileType: KfFileTypeEnum;
  kfContainType: KfContainTypeEnum;
  tmKeywords: string[];
  kfDefaultLogicalOperator: KfDefaultLogicalOperatorEnum;
  kfCollaborationActionType: string; // [103]
  kfNonCollaborationActionType: string; // [6, 103, 109]
}

export interface KeywordExtractionFilter {
  cfgIndex?: string; //update时才有
  companyId?: string; //update时才有
  kfEnabled: EnableDisableStatus;
  kfContainRules: KfContainRule[];
  kfReplacementText: string;
  kfCollaborationActionType: KfCollaborationActionTypeEnum;
  kfCollaborationNotifyType: EnableDisableStatus;
  kfNonCollaborationActionType: KfCollaborationActionTypeEnum;
  kfNonCollaborationNotifyType: EnableDisableStatus;
  notification: Notification;
}
export enum SlfOpenShareLinkActionTypeEnum {
  removeLink = ActionEnum.removeLink,
  pass = ActionEnum.pass,
}

export interface SlfException {
  slfExceptionUsers: Targets;
  slfExceptionPaths: string[];
}
export interface ShareLinkFilter {
  cfgIndex?: string; //update时才有
  companyId?: string; //update时才有
  readonly slEnabled: EnableDisableStatus; // hardcode, do not change in UI
  slfExceptionList: SlfException[];
  slfNotificationInstanceCfgsetting?: string; // hardcode, do not change in UI
  slfOpenShareLinkActionType: ActionEnum;
  slfOpenShareLinkNotifyType: EnableDisableStatus;
  notification: Notification;
}

export type Filter<Policy> = Policy extends ExchangeOnlinePolicyItem
  ? typeof PolicyFilters
  : Policy extends DlpPolicyItem
    ? typeof DlpPolicyFilters
    : never;

/***********************************************************************************************************************
 * Checked Skipped Users
 ***********************************************************************************************************************
 */
export interface SkippedUsersInfo {
  absentUsers: SkippedUserInfo[];
  failedUsers: SkippedUserInfo[];
  invalidUsers: SkippedUserInfo[];
  otherUsers: SkippedUserInfo[];
}

export interface SkippedUserInfo {
  displayname: string;
  id: string;
  status: string;
}
